import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';

import {EEventSource} from 'coreClasses/events/IEvents';
import {ESearchType} from 'coreClasses/search/ISearch';
import {DEFAULT_KEY} from 'coreClasses/search/SearchMgr';
import SearchModel from 'coreClasses/search/SearchModel';
import {TScrollBehavior} from 'coreClasses/view/IViewportMgr';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import LoaderComponent from 'coreComponents/LoaderComponent';
import {useScrollRestoration} from 'coreComponents/ScrollRestoration';
import withSSRPage from 'coreComponents/withSSRPage';
import {
	useClientServerEffect,
	useCustomerMgr,
	useCustomerPersonalizationKey,
	useHistory,
	useIsFirstRender,
	useLocalesMgr,
	useLocation,
	usePageInit,
	usePrefetchCmsContent,
	useScroll,
	useSearchMgrInstance,
	useSetUI,
	useSuspenseCmsContent,
	useUI,
	useWindowResize,
} from 'coreHooks';
import {useCategory} from 'coreHooks/categories';
import useLanguageCode from 'coreHooks/locales/useLanguageCode';
import usePLPData from 'coreHooks/pages/plp/usePLPData';
import usePlpURLModifier from 'coreHooks/pages/plp/usePlpURLModifier';
import useSeoLinks from 'coreHooks/pages/plp/useSeoLinks';
import useChangeEffect from 'coreHooks/useChangeEffect';
import buildPageMetadata from 'coreUtils/buildPageMetadata';
import {absoluteUrl, appendParamsToURL} from 'coreUtils/url';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Contentful from 'brand/app/contentful/components/Contentful';
import Button from 'brandComponents/Button';
import Icon from 'brandComponents/Icon';
import InTheMoodForFlyout from 'brandComponents/InTheMoodFor/InTheMoodForFlyout';
import {
	DEFAULT_SEARCH_PARAMS,
	FIRST_PAGE,
	MAX_CACHE_AGE,
	QUICK_SEARCH_PAGE_INSTANCE_KEY,
	SEARCH_PAGE_TYPE,
} from 'brandConstants';
import {useHandlePageEvents} from 'brandHooks/useHandlePageEvents';

const fallback = <LoaderComponent />;
const PageNotFound = lazyComponent(() => import('../../pages/page-not-found'), {fallback});

import {RouteInfo} from 'sfccAPI/interfaces';

import {lazyComponent} from 'coreUtils/adapters/lazy';

import useITMF from 'brand/app/pages/category/useITMF';

import {CategoryProvider} from './context/CategoryContext';
import CategoryDefault from './Subcomponents/CategoryDefault';
import {ICategoryPageContent} from './ICategory';
import {ICategoryProps} from './ICategory';

import styles from './Category.module.scss';

const CategoryContent = ({routeInfo, content, localizedContent}: {
	routeInfo: RouteInfo,
	content: ICategoryPageContent,
	localizedContent: ICategoryPageContent,
}) => {
	const prevAlternativeCgid = routeInfo?.prevAlternativeCgid;
	const {managerRepository} = useGeneralProvider();
	const location = useLocation();
	const languageCode = useLanguageCode();
	const {searchInstanceKey, searchType} = useMemo(() => {
		if (content?.type === SEARCH_PAGE_TYPE) {
			return {
				searchInstanceKey: QUICK_SEARCH_PAGE_INSTANCE_KEY,
				searchType: ESearchType.QUICK_SEARCH,
			};
		} else {
			return {
				searchInstanceKey: DEFAULT_KEY,
				searchType: ESearchType.PAGE,
			};
		}
	}, [content?.type]);
	const itmfContentOptions = {
		contentType: 'navigationItem',
		config: {
			variables: {
				internalTitle: 'In The Mood For',
				locale: [languageCode],
			},
		},
		collectionKey: 'navigationItemCollection',
	};

	// Starts the ITMF request now: `useSuspenseCmsContent` below only runs once the PLP data resolved.
	usePrefetchCmsContent(itmfContentOptions);

	const {cgid, data, query, queryPage, pageToLoad, refinements, isQueryTooShort, searchQuery, srule} =
		usePLPData({
			routeInfo,
			searchInstanceKey,
			searchType,
			localizedContent,
		});
	const seoMgr = managerRepository.getSeoMgr();
	const searchMgr = useSearchMgrInstance({
		key: searchInstanceKey,
		searchType,
	});
	const searchModel: SearchModel = searchMgr.getStoredSearchModel();
	const canonical = useMemo(() => {
		let params = {};

		if (queryPage) {
			params = {page: queryPage.toString(10)};
		}

		return appendParamsToURL(absoluteUrl(location.pathname), params);
	}, [location.pathname, queryPage, seoMgr]);
	const seoLinks = useSeoLinks(pageToLoad, searchModel?.getLastPageNumber());
	const queryString = useMemo(() => new URLSearchParams(query).toString(), [query]);
	const seoMetadata = useMemo(() => {
		const metadata = [];

		if (content?.metadata) {
			const pageMeta = buildPageMetadata(content.metadata);

			if (pageMeta) {
				metadata.push(pageMeta);
			}
		}

		if (routeInfo?.metadata) {
			metadata.push(routeInfo.metadata);
		}

		return metadata;
	}, [content, routeInfo]);

	usePageInit({
		route: `Search-Show?${queryString}`,
		cache: routeInfo?.expirationTime || MAX_CACHE_AGE,
		seo: {
			metadata: seoMetadata,
			pageUrls: routeInfo?.pageUrls,
			jsonld: routeInfo?.jsonld,
			canonical,
			links: seoLinks,
		},
	});

	const {data: itmfData} = useSuspenseCmsContent(itmfContentOptions);

	const {processedITMFData, isITMF} = useITMF(itmfData, cgid);

	if (data?.redirected) {
		return null;
	}

	if (data?.notFound) {
		return <PageNotFound />;
	}

	return (
		<CategoryInner
			data={localizedContent}
			initialRefinements={refinements}
			initialSorting={srule}
			initialPage={pageToLoad}
			searchQuery={searchQuery}
			cgid={cgid}
			prevAlternativeCgid={prevAlternativeCgid}
			searchInstanceKey={searchInstanceKey}
			searchType={searchType}
			isQueryTooShort={isQueryTooShort}
			isITMF={isITMF}
			ITMFData={processedITMFData}
			searchMgr={searchMgr}
			searchModel={searchModel}
		/>
	);
};

const CategoryInner = (
	{
		data,
		initialRefinements,
		initialSorting,
		initialPage,
		searchQuery,
		cgid,
		prevAlternativeCgid,
		searchInstanceKey,
		searchType,
		isITMF,
		ITMFData,
		behaviour = 'smooth',
		scrollOffset,
		isRequiresLoginSSR,
		searchMgr,
		searchModel,
	}: ICategoryProps) => {
	const customer = useCustomerMgr().getCurrentCustomer();
	const {isReady: isCustomerReady, isRegistered} = customer;

	const {managerRepository} = useGeneralProvider();
	const customerPersonalizationKey = useCustomerPersonalizationKey();
	const [lastCustomerPersonalizationKey, setLastCustomerPersonalizationKey] = useState<string | undefined>(customerPersonalizationKey);
	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const categoryModel = useCategory(cgid);
	const beforeCategoryModel = useCategory(prevAlternativeCgid);
	const location = useLocation();
	const history = useHistory();
	const setUI = useSetUI();
	const rotationalBanner = useUI('rotationalBanner');
	const isFirstRender = useIsFirstRender();
	const isRequiresLoginEnabled = useMemo(() => {
		const requiresLogin = categoryModel?.isRequiresLoginInHierarchy() ?? isRequiresLoginSSR;

		return requiresLogin && isCustomerReady && !isRegistered;
	}, [isCustomerReady, isRegistered, isRequiresLoginSSR, categoryModel]);
	const currentUrl = beforeCategoryModel?.getCategoryUrl() || categoryModel?.getCategoryUrl();
	const initialHeight = useRef<number>(0);
	const selectedRefinements = searchModel?.getSelectedRefinements();

	const {setOnRestoration} = useScrollRestoration(true) || {};

	const {onScroll, unBind} = useScroll();
	const windowResize = useWindowResize();
	const [shouldDisplayButton, setShouldDisplayButton] = useState(false);
	const [isPrevPageLoaded, setIsPrevPageLoaded] = useState(initialPage === FIRST_PAGE);

	const {setPageUrl} = usePlpURLModifier({
		searchModel,
		searchMgr,
		initialPage,
		currentUrl,
	});

	const setVisibility = useCallback(() => {
		const currentScrollOffset = scrollOffset || windowResize.height / 4;

		if (ViewportMgr.getScrollPosition().scrollY > currentScrollOffset) {
			setShouldDisplayButton(true);
		} else {
			setShouldDisplayButton(false);
		}
	}, [scrollOffset, windowResize]);

	const scrollTop = useCallback(() => {
		ViewportMgr.scrollTo(0, 0, behaviour as TScrollBehavior);
	}, []);

	useEffect(() => {
		setUI.setUI({requiresLoginFeature: {enabled: isRequiresLoginEnabled}});
		!isRequiresLoginEnabled && setUI.removeHtmlCssClasses(['requires-login-lock-scroll']);

		return () => {
			setUI.setUI({requiresLoginFeature: {enabled: false}});
			setUI.removeHtmlCssClasses(['requires-login-lock-scroll']);
		};
	}, [isRequiresLoginEnabled]);

	useEffect(() => {
		const index = onScroll(setVisibility);

		return () => unBind(index);
	}, []);

	if (searchModel?.getSearchRedirectUrl() && location.pathname !== searchModel.getSearchRedirectUrl()) {
		history.push(searchModel.getSearchRedirectUrl());
	}

	useHandlePageEvents(
		{
			pageType: EEventSource.PLP,
			pageSpecificData: {
				productCategory: categoryModel?.getId(),
				productSubCategory: categoryModel?.getParentCategoryId(),
			},
		},
		[categoryModel],
	);

	useClientServerEffect(() => {
		managerRepository.getUiMgr().addHtmlCssClasses(['plp']);

		return () => {
			managerRepository.getUiMgr().removeHtmlCssClasses(['plp']);
		};
	}, []);

	// Search state of the page: set up on the first mount only. The keep-alive (`<Activity>`) runs this effect
	// again when the PLP is shown again: its search, pages and scroll are still there; only the search query,
	// cleared when the page was hidden, is set back.
	const isSearchSetUpRef = useRef(false);

	useEffect(() => {
		if (isSearchSetUpRef.current) {
			searchMgr.setSearchQuery(searchQuery);

			return () => {
				searchMgr.setSearchQuery('');
			};
		}

		isSearchSetUpRef.current = true;

		if (searchMgr.getUserSelectedRefinements()?.cgid?.[0] !== cgid || !cgid) {
			searchMgr.setUserSelectedPage(initialPage);
			setPageUrl(currentUrl);
			searchMgr.setPageNumber(initialPage);
			searchMgr.setUserSelectedSorting(initialSorting);
			searchMgr.setUserSelectedRefinements(selectedRefinements || initialRefinements);
			searchMgr.setSearchQuery(searchQuery);
			searchMgr.setLimit(DEFAULT_SEARCH_PARAMS.limit);
		}

		if (initialPage > FIRST_PAGE && initialPage === searchModel.getLowestLoadedPageNumber()) {
			const shellHeight = document.querySelector('main')?.scrollHeight || 0;
			const MAX_RAF_ATTEMPTS = 60;
			setUI.setHeader({locked: true});

			const waitForInitialProducts = (attempts = 0) => {
				const currentHeight = document.querySelector('main')?.scrollHeight || 0;

				if (currentHeight <= shellHeight && attempts < MAX_RAF_ATTEMPTS) {
					requestAnimationFrame(() => waitForInitialProducts(attempts + 1));

					return;
				}

				initialHeight.current = currentHeight;

				if (searchQuery) {
					searchMgr.enableWildCardQuery(true);
				}

				searchMgr
					.search({page: initialPage - 1})
					.then(() => {
						const waitForPrevPage = (prevAttempts = 0) => {
							const heightAfter = document.querySelector('main')?.scrollHeight || 0;

							if (heightAfter <= initialHeight.current && prevAttempts < MAX_RAF_ATTEMPTS) {
								requestAnimationFrame(() => waitForPrevPage(prevAttempts + 1));

								return;
							}

							const scrollOffsetVal = heightAfter - initialHeight.current;
							ViewportMgr.scrollTo(scrollOffsetVal);
							setIsPrevPageLoaded(true);
						};
						requestAnimationFrame(() => waitForPrevPage());
					});
			};
			requestAnimationFrame(() => waitForInitialProducts());
		} else {
			setOnRestoration?.(() => {
				setIsPrevPageLoaded(true);
				setOnRestoration?.(null);
			});
		}

		return () => {
			searchMgr.setSearchQuery('');
		};
	}, []);

	useEffect(() => {
		if (!customerPersonalizationKey) {
			return () => null;
		}

		if (isFirstRender || lastCustomerPersonalizationKey === customerPersonalizationKey) {
			return () => null;
		}

		setLastCustomerPersonalizationKey(customerPersonalizationKey);

		searchMgr.resetToFirstPage();
		searchMgr.setUserSelectedPage(FIRST_PAGE);
		searchMgr.search();
	}, [customerPersonalizationKey]);

	useChangeEffect(() => {
		searchMgr.setChangedLocale(true);
		searchMgr.saveSelectedRefinements();
	}, [currentLocale.getLanguageCode()]);

	useEffect(() => {
		if (searchMgr.getSearchQuery()) {
			const url: string = localesMgr.updateLinkWithLocale('/search');

			setPageUrl(url);
		}
	}, [searchMgr.getSearchQuery()]);

	return (
		<CategoryProvider
			searchInstanceKey={searchInstanceKey}
			searchType={searchType}
			cgid={cgid}
			isITMF={isITMF}
			ITMFData={ITMFData}
			isPrevPageLoaded={isPrevPageLoaded}
			initialPage={initialPage || FIRST_PAGE}
			customElements={data?.customElementIncludeCollection}
			customElementsConfigs={data?.productsGridWidgetsCollection}
			customElementsHideInSmallGrid={data?.hideInSmallGrid}
		>
			<div
				className={cn(styles['category-login-overlay'], {
					[styles['category-login-overlay__visible']]: isRequiresLoginEnabled,
				})}
			/>
			<InTheMoodForFlyout />
			<div
				className={cn(styles['category'], 'category', {
					[styles['category__search']]: searchQuery,
					[styles['rotational-banner__hidden']]: !rotationalBanner,
				})}
			>
				<Button classNames={cn(`scroll_top_button ${shouldDisplayButton ? 'visible' : ''}`)} onClick={scrollTop}>
					<Icon name="arrow-up" height={40} width={40} />
				</Button>

				{data?.page ? <Contentful initialData={data?.page} /> : <CategoryDefault />}
			</div>
		</CategoryProvider>
	);
};

export default withSSRPage(CategoryContent, 'category');
